import React, { useContext, useState, useMemo } from 'react';
import { AppContext } from '../../context/AppContext';
import { formatMailDateTime } from '../../utils/sanitize';
import ApprovalActionModal from '../../components/ApprovalActionModal';
import Avatar from '../../components/Avatar';
import { generateEmailSummary } from '../../utils/aiSummarizer';
import { 
  Mail, 
  Calendar, 
  Receipt, 
  Users, 
  ArrowRight, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Sparkles,
  Camera
} from 'lucide-react';

const MobileDashboardTab = ({ setActiveTab, onNavigateUnreadMail }) => {
  const { currentUser, users, emails, approvals, contracts, handleApprovalAction, getAutoApprovalLine, groupMails, blockedSenders } = useContext(AppContext);
  const user = users.find(u => u.UserID === currentUser?.UserID) || currentUser;

  // Selected approval for quick action modal
  const [selectedApprovalForAction, setSelectedApprovalForAction] = useState(null);

  // Approvals in which current user is in the approval line
  const pendingApprovalsForMe = useMemo(() => {
    if (!currentUser || !approvals) return [];
    const myId = currentUser.UserID;
    const myName = currentUser.Name;
    const myEmail = (currentUser.Email || '').toLowerCase();

    return approvals
      .filter(a => a.Status === 'PENDING' || a.Status === 'CANCEL_REQUESTED')
      .map(a => {
        const applicantObj = users?.find(u => u.UserID === a.ApplicantID) || { UserID: a.ApplicantID, Department: a.ApplicantDept };
        const stages = (a.ApprovalStages && a.ApprovalStages.length > 0)
          ? a.ApprovalStages
          : (typeof getAutoApprovalLine === 'function' ? getAutoApprovalLine(applicantObj) : []);

        const currentStep = Number(a.CurrentStep || 1);
        const currentStage = stages.find(s => Number(s.Step) === currentStep);
        const currentApproverName = currentStage?.ApproverName || currentStage?.RoleName || `${currentStep}차 결재자`;

        // Is current user in the approval line? (Strict check: must be in stages)
        const isInLine = stages.some(s => 
          s.ApproverID === myId || 
          s.ApproverName === myName || 
          (s.ApproverEmail && s.ApproverEmail.toLowerCase() === myEmail)
        );

        if (!isInLine) return null;

        // Is it strictly current user's turn?
        const isMyTurn = currentStage && (
          currentStage.ApproverID === myId ||
          currentStage.ApproverName === myName ||
          (currentStage.ApproverEmail && currentStage.ApproverEmail.toLowerCase() === myEmail)
        );

        return {
          ...a,
          calculatedStages: stages,
          currentStep,
          currentApproverName,
          isMyTurn: Boolean(isMyTurn)
        };
      })
      .filter(Boolean);
  }, [approvals, currentUser, users, getAutoApprovalLine]);

  const myTurnCount = pendingApprovalsForMe.filter(a => a.isMyTurn).length;

  // Check if current user has an approved resignation
  const myApprovedResignation = (contracts || []).find(c => {
    if (!currentUser) return false;
    const myId = currentUser.UserID;
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myName = currentUser.Name;
    const isMine = (c.UserID && c.UserID === myId) || 
                   (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) || 
                   (c.UserName === myName);
    const isResig = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA');
    const isApproved = c.Status === 'APPROVED' || c.Status === 'SIGNED' || c.ResignationStatus === 'APPROVED';
    return isMine && isResig && isApproved;
  });

  const resignationDDayInfo = (() => {
    if (!myApprovedResignation) return null;
    const resigDateStr = myApprovedResignation.ResignationDate || myApprovedResignation.ContractEndDate;
    if (!resigDateStr) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(resigDateStr);
    target.setHours(0, 0, 0, 0);

    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let label = '';
    let badgeClass = '';
    let subMessage = '';

    if (diffDays > 0) {
      label = `D-${diffDays}`;
      badgeClass = 'bg-rose-500 text-white';
      subMessage = `퇴사 예정일(${resigDateStr})까지 ${diffDays}일 남았습니다.`;
    } else if (diffDays === 0) {
      label = 'D-Day';
      badgeClass = 'bg-rose-600 text-white animate-pulse';
      subMessage = `오늘(${resigDateStr})이 최종 퇴사일입니다.`;
    } else {
      label = `D+${Math.abs(diffDays)}`;
      badgeClass = 'bg-slate-600 text-white';
      subMessage = `퇴사일(${resigDateStr})이 경과하였습니다.`;
    }

    return {
      resignationDate: resigDateStr,
      diffDays,
      dDayLabel: label,
      badgeClass,
      subMessage,
      contract: myApprovedResignation
    };
  })();

  // Unread emails count in Received Mailbox (Aliases & OwnerEmail included, strictly inbox)
  const myInboxEmails = useMemo(() => {
    if (!currentUser || !emails) return [];
    const raw = (currentUser?.Email || '').toLowerCase().trim();
    const aliasList = (currentUser?.Aliases || '').toLowerCase().split(',').map(a => a.trim()).filter(Boolean);
    const allUserAddresses = [raw, ...aliasList].filter(Boolean);
    const blocked = (blockedSenders || []).map(b => (b || '').toLowerCase().trim());

    let trashMap = {};
    try {
      const savedTrash = localStorage.getItem('clou_trash_emails_v1');
      if (savedTrash) trashMap = JSON.parse(savedTrash);
    } catch (e) {
      console.warn('Mobile Dashboard failed to parse trash map:', e);
    }

    return emails.filter(e => {
      if (!e) return false;
      // 1. Trashed or Spam check
      const isTrashed = Boolean(trashMap[e.EmailID]) || e.Folder === 'trash' || e.IsDeleted === true;
      if (isTrashed) return false;
      if (e.IsSpam || e.Folder === 'spam') return false;
      const sender = (e.Sender || '').toLowerCase();
      if (blocked.some(b => b && sender.includes(b))) return false;

      // 2. Sent email check (exclude pure sent emails from inbox)
      const isMeSender = allUserAddresses.some(a => sender.includes(a));
      if ((e.Folder === 'sent' || isMeSender) && !e.EmailID?.includes('-inbox')) {
        const rec = (e.Recipient || '').toLowerCase();
        const isSelf = allUserAddresses.some(a => rec.includes(a));
        if (!isSelf) return false;
      }

      // 3. Must be addressed to me, my alias, or broadcast
      const rec = (e.Recipient || '').toLowerCase();
      const cc = (e.Cc || '').toLowerCase();
      const bcc = (e.Bcc || '').toLowerCase();
      const owner = (e.OwnerEmail || '').toLowerCase().trim();

      const isMeRecipient = allUserAddresses.some(a => rec.includes(a) || cc.includes(a) || bcc.includes(a) || owner.includes(a)) ||
                            rec.includes('all@clouflake.com') || cc.includes('all@clouflake.com');

      if (!isMeRecipient) {
        if (Array.isArray(groupMails)) {
          const isAssigned = groupMails.some(group => {
            const groupAddr = (group.GroupEmail || '').toLowerCase();
            if (!rec.includes(groupAddr) && !cc.includes(groupAddr)) return false;
            const assignees = (group.Assignees || []).map(a => a.toLowerCase().trim());
            return allUserAddresses.some(a => assignees.includes(a));
          });
          if (!isAssigned) return false;
        } else {
          return false;
        }
      }

      return true;
    });
  }, [emails, currentUser, blockedSenders, groupMails]);

  const unreadCount = myInboxEmails.filter(e => !e.IsRead).length;

  // Latest received email (prioritizing unread if available, otherwise absolute latest)
  const latestMail = useMemo(() => {
    if (!myInboxEmails || myInboxEmails.length === 0) return null;
    const sorted = [...myInboxEmails].sort((a, b) => new Date(b.Timestamp || b.DeliveredAt || 0) - new Date(a.Timestamp || a.DeliveredAt || 0));
    const firstUnread = sorted.find(e => !e.IsRead);
    return firstUnread || sorted[0];
  }, [myInboxEmails]);

  const latestMailSummary = useMemo(() => {
    if (!latestMail) return null;
    return generateEmailSummary(latestMail);
  }, [latestMail]);

  // Pending Approvals for current user or as approver
  const myPendingApprovals = approvals.filter(a => a.ApplicantID === currentUser?.UserID && a.Status === 'PENDING');

  const leaveRemaining = user.RemainingLeave !== undefined ? user.RemainingLeave : (user.AnnualLeave || 15);

  return (
    <div className="p-4 space-y-4 text-left pb-24 bg-[#F4F5F9] min-h-full">
      {/* 0. Mobile Resignation D-Day Guidance Banner */}
      {resignationDDayInfo && (
        <div 
          onClick={() => setActiveTab('contract')}
          className="p-3.5 bg-gradient-to-r from-rose-900 via-slate-900 to-rose-950 text-white rounded-3xl shadow-sm border border-rose-500/30 flex items-center justify-between gap-3 cursor-pointer active:scale-98 transition-all"
        >
          <div className="space-y-1 overflow-hidden">
            <div className="flex items-center gap-1.5">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${resignationDDayInfo.badgeClass}`}>
                {resignationDDayInfo.dDayLabel}
              </span>
              <span className="text-xs font-black text-rose-200">사직원 최종 승인</span>
            </div>
            <p className="text-[11px] text-rose-100/90 font-medium truncate">
              {resignationDDayInfo.subMessage}
            </p>
          </div>
          <div className="p-2 bg-rose-600 rounded-xl text-white flex-shrink-0">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      )}

      {/* 1. Employee Welcome Profile Card */}
      <div className="p-4 bg-gradient-to-br from-[#5C5FEF] via-indigo-600 to-[#1D212C] text-white rounded-3xl shadow-primary space-y-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-xs flex items-center justify-center overflow-hidden border border-white/30 shadow-inner flex-shrink-0">
              {user.ProfileImage || (typeof user.AvatarConfig === 'string' && (user.AvatarConfig.startsWith('http') || user.AvatarConfig.startsWith('data:image'))) ? (
                <img
                  src={user.ProfileImage || user.AvatarConfig}
                  alt={user.Name}
                  className="w-full h-full object-cover rounded-2xl"
                />
              ) : user.AvatarConfig ? (
                <Avatar config={user.AvatarConfig} size={44} className="border-none shadow-none" />
              ) : (
                <Avatar config={null} size={44} className="border-none shadow-none" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-black text-base">{user.Name}</span>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full font-bold">{user.JobTitle || '사원'}</span>
              </div>
              <p className="text-xs text-indigo-100/90 mt-0.5 font-medium">{user.Department || '부서 미정'} · {user.Email}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2.5 border-t border-white/20 text-center">
          <div 
            onClick={() => setActiveTab('leave')}
            className="bg-white/10 hover:bg-white/20 active:scale-95 transition-all rounded-2xl p-2.5 cursor-pointer border border-white/10"
          >
            <span className="text-[11px] text-indigo-100 block font-semibold">잔여 연차</span>
            <strong className="text-lg font-black">{leaveRemaining}일</strong>
          </div>
          <div 
            onClick={() => {
              if (typeof onNavigateUnreadMail === 'function') {
                onNavigateUnreadMail();
              } else {
                setActiveTab('webmail');
              }
            }}
            className="bg-white/10 hover:bg-white/20 active:scale-95 transition-all rounded-2xl p-2.5 cursor-pointer border border-white/10 shadow-xs group"
          >
            <span className="text-[11px] text-indigo-100 block font-semibold group-hover:text-amber-200">안읽은 메일 ❯</span>
            <strong className="text-lg font-black text-amber-300">{unreadCount}통</strong>
          </div>
        </div>
      </div>

      {/* 1.5 Horizontal Swipeable AI Smart Summary Cards (Pastel Carousel) */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-center px-1">
          <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
            ✨ AI 스마트 요약 브리핑
          </span>
          <span className="text-[10px] text-indigo-600 font-bold bg-indigo-50 px-1.5 py-0.5 rounded-md">
            좌우 스와이프 ➔
          </span>
        </div>
        <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory pb-1.5 scrollbar-none -mx-4 px-4">
          {/* Card 1: Blue AI Mail Summary */}
          <div 
            onClick={() => setActiveTab('webmail')}
            className="min-w-[280px] max-w-[290px] bg-[#F6F8FF] rounded-3xl p-4 border border-indigo-100 shadow-2xs snap-center flex-shrink-0 space-y-2 cursor-pointer active:scale-98 transition-transform flex flex-col justify-between"
          >
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[#5C5FEF] font-extrabold text-xs flex items-center gap-1.5">
                  <img 
                    src="/assets/ai_robot.jpg" 
                    alt="AI Robot" 
                    className="w-5 h-5 rounded-full object-cover shadow-2xs border border-indigo-200 inline-block flex-shrink-0" 
                  />
                  <span>AI 최신 메일 요약</span>
                </span>
                <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${
                  latestMail && !latestMail.IsRead 
                    ? 'bg-rose-50 text-rose-600 border-rose-200 animate-pulse' 
                    : 'bg-white text-indigo-600 border-indigo-100'
                }`}>
                  {latestMail && !latestMail.IsRead ? '신규 수신' : unreadCount > 0 ? `안읽음 ${unreadCount}건` : '최신 요약'}
                </span>
              </div>

              {latestMailSummary ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-slate-800 font-extrabold text-xs truncate">
                    <span className="text-indigo-600 flex-shrink-0">[{latestMailSummary.sender?.split('<')[0]?.trim() || '발신자'}]</span>
                    <span className="truncate">{latestMailSummary.subject}</span>
                  </div>
                  <div className="text-[11px] text-slate-600 bg-white/80 p-2 rounded-2xl border border-indigo-100/70 leading-relaxed font-medium space-y-0.5">
                    <p className="line-clamp-2">
                      <strong className="text-slate-800">📌 안건:</strong> {latestMailSummary.agenda}
                    </p>
                    {latestMailSummary.actionItem && (
                      <p className="line-clamp-1 text-slate-700">
                        <strong className="text-indigo-600">⚡ 요청:</strong> {latestMailSummary.actionItem}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-slate-500 font-medium py-1">
                  수신된 메일이 없습니다. 새 메일 도착 시 자동 브리핑됩니다.
                </p>
              )}
            </div>

            <div className="text-[10px] font-extrabold text-[#5C5FEF] flex items-center gap-1 pt-0.5">
              <span>메일함 열기 ➔</span>
            </div>
          </div>

          {/* Card 2: Green Action Items */}
          <div 
            onClick={() => setActiveTab(pendingApprovalsForMe.length > 0 ? 'approval' : 'leave')}
            className="min-w-[270px] max-w-[280px] bg-[#F4FAF7] rounded-3xl p-4 border border-emerald-100 shadow-2xs snap-center flex-shrink-0 space-y-2 cursor-pointer active:scale-98 transition-transform"
          >
            <div className="flex items-center justify-between">
              <span className="text-emerald-700 font-extrabold text-xs flex items-center gap-1.5">
                <span>✅</span> 업무 결재 & 연차
              </span>
              <span className="text-[10px] font-extrabold text-emerald-700 bg-white px-2 py-0.5 rounded-full border border-emerald-100">
                {pendingApprovalsForMe.length}건 대기
              </span>
            </div>
            <p className="text-[11px] text-slate-700 font-semibold leading-relaxed line-clamp-2">
              {pendingApprovalsForMe.length > 0 
                ? `결재 대기 중인 문서가 ${pendingApprovalsForMe.length}건 있습니다. 지연 없이 승인하세요.` 
                : `현재 결재 대기 문서가 없습니다. 실잔여 연차는 ${leaveRemaining}일입니다.`}
            </p>
            <div className="text-[10px] font-extrabold text-emerald-700 flex items-center gap-1">
              <span>{pendingApprovalsForMe.length > 0 ? '결재 승인하기 ➔' : '연차 캘린더 보기 ➔'}</span>
            </div>
          </div>

          {/* Card 3: Purple Contract Insight */}
          <div 
            onClick={() => setActiveTab('contract')}
            className="min-w-[270px] max-w-[280px] bg-[#F9F6FE] rounded-3xl p-4 border border-purple-100 shadow-2xs snap-center flex-shrink-0 space-y-2 cursor-pointer active:scale-98 transition-transform"
          >
            <div className="flex items-center justify-between">
              <span className="text-purple-700 font-extrabold text-xs flex items-center gap-1.5">
                <span>💡</span> AX 전자계약
              </span>
              <span className="text-[10px] font-extrabold text-purple-700 bg-white px-2 py-0.5 rounded-full border border-purple-100">
                보안 체결
              </span>
            </div>
            <p className="text-[11px] text-slate-700 font-semibold leading-relaxed line-clamp-2">
              사내 근로계약서, 연봉협상서 및 보안 서약이 클라우드 보관함에 암호화 보관 중입니다.
            </p>
            <div className="text-[10px] font-extrabold text-purple-700 flex items-center gap-1">
              <span>계약서 보관함 ➔</span>
            </div>
          </div>
        </div>
      </div>

      {/* 1-1. Mobile Approvals Pending for Current User (For Approvers in Approval Line) */}
      {pendingApprovalsForMe.length > 0 && (
        <div className="bg-gradient-to-r from-amber-500/15 via-amber-50 to-indigo-50 border-2 border-amber-300 rounded-3xl p-4 shadow-card space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-amber-500 text-white rounded-lg">
                <CheckCircle className="w-4 h-4" />
              </span>
              <span className="font-extrabold text-xs text-slate-900">
                나의 결재 대기 문서 ({pendingApprovalsForMe.length}건)
              </span>
            </div>
            {myTurnCount > 0 ? (
              <span className="text-[10px] bg-rose-600 text-white px-2 py-0.5 rounded-full font-black animate-pulse">
                내 차례 {myTurnCount}건
              </span>
            ) : (
              <span className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">
                진행 대기
              </span>
            )}
          </div>

          <div className="space-y-2">
            {pendingApprovalsForMe.slice(0, 4).map(appr => {
              const isLeave = appr.Type === 'LEAVE';
              const isExpense = appr.Type === 'EXPENSE';
              return (
                <div
                  key={appr.ApprovalID}
                  onClick={() => setSelectedApprovalForAction(appr)}
                  className={`bg-white p-3 rounded-xl border shadow-2xs space-y-1.5 active:bg-slate-50 cursor-pointer transition-all ${
                    appr.isMyTurn ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200 bg-slate-50/50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-black ${
                        isLeave ? 'bg-teal-50 text-teal-700' : isExpense ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'
                      }`}>
                        {isLeave ? '연차' : isExpense ? '경비' : '일반'}
                      </span>
                      <span className="text-xs font-black text-slate-900">{appr.ApplicantName}</span>
                      <span className="text-[10px] text-slate-500">({appr.ApplicantDept})</span>
                    </div>
                    {appr.isMyTurn ? (
                      <span className="text-[11px] font-black text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-md flex items-center gap-1 animate-pulse">
                        🔴 결재 승인/반려 ❯
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">
                        ⏳ {appr.currentStep}차 [{appr.currentApproverName}] ❯
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-700 font-medium truncate">
                    {isLeave ? `${appr.Details?.split('(')[0] || '연차'} (${appr.LeaveDays}일) · ${appr.StartDate}` : isExpense ? `${Number(appr.Amount || appr.ExpenseAmount || 0).toLocaleString()}원 · ${appr.Details || appr.ExpenseDetails || '경비'}` : appr.Details}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. Quick Action Grid */}
      <div className="space-y-2">
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block px-1">빠른 업무 바로가기</span>
        <div className="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={() => setActiveTab('webmail')}
            className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-2xs text-left space-y-2 active:bg-slate-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-xs text-slate-900 flex items-center justify-between">
                <span>사내 메일</span>
                {unreadCount > 0 && (
                  <span className="text-[10px] px-1.5 py-0.2 bg-indigo-600 text-white rounded-full font-extrabold">
                    {unreadCount}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">받은 편지함 확인</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('leave')}
            className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-2xs text-left space-y-2 active:bg-slate-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <Calendar className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-xs text-slate-900">연차 신청</div>
              <p className="text-[11px] text-slate-400 mt-0.5">잔여 {leaveRemaining}일 사용</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('expense')}
            className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-2xs text-left space-y-2 active:bg-slate-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-xs text-slate-900">영수증 촬영 청구</div>
              <p className="text-[11px] text-slate-400 mt-0.5">OCR 자동 금액 기입</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('addressbook')}
            className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-2xs text-left space-y-2 active:bg-slate-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-xs text-slate-900">임직원 주소록</div>
              <p className="text-[11px] text-slate-400 mt-0.5">사내 연락처 검색</p>
            </div>
          </button>
        </div>
      </div>

      {/* 3. Recent Unread Emails Glance */}
      <div className="space-y-2">
        <div className="flex justify-between items-center px-1">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">최근 수신 메일</span>
          <button
            type="button"
            onClick={() => setActiveTab('webmail')}
            className="text-xs text-indigo-600 font-bold flex items-center gap-0.5"
          >
            <span>전체보기</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden shadow-2xs">
          {myInboxEmails.slice(0, 3).map(email => (
            <div
              key={email.EmailID}
              onClick={() => setActiveTab('webmail')}
              className="p-3 flex items-center gap-3 active:bg-slate-50 cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center flex-shrink-0 text-xs">
                {email.Sender?.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <span className={`text-xs truncate ${!email.IsRead ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                    {email.Sender}
                  </span>
                  <span className="text-[10px] text-slate-400 font-mono">
                    {formatMailDateTime(email.Timestamp || email.DeliveredAt || email.CreatedAt || email.Date)}
                  </span>
                </div>
                <div className={`text-xs truncate ${!email.IsRead ? 'font-black text-indigo-700' : 'text-slate-600'}`}>
                  {email.Subject}
                </div>
              </div>
            </div>
          ))}
          {myInboxEmails.length === 0 && (
            <div className="p-6 text-center text-xs text-slate-400 font-medium">수신된 메일이 없습니다.</div>
          )}
        </div>
      </div>

      {/* 4. My Approvals Glance */}
      {myPendingApprovals.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block px-1">진행 중인 결재 요청</span>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 font-bold text-xs">
              <Clock className="w-4 h-4 text-amber-600" />
              <span>결재 대기 중인 항목 ({myPendingApprovals.length}건)</span>
            </div>
            <div className="space-y-1.5">
              {myPendingApprovals.slice(0, 2).map(app => (
                <div key={app.ApprovalID} className="bg-white/80 p-2.5 rounded-xl text-xs flex justify-between items-center">
                  <div>
                    <span className="font-bold text-slate-900">{app.Type === 'EXPENSE' ? '경비 청구' : '연차 신청'}</span>
                    <span className="text-slate-500 block text-[11px]">{app.Details || `${app.Days || 1}일`}</span>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md">대기 중</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Approval Action Modal */}
      {selectedApprovalForAction && (
        <ApprovalActionModal
          approval={selectedApprovalForAction}
          onClose={() => setSelectedApprovalForAction(null)}
          onAction={handleApprovalAction}
          currentUser={currentUser}
          isMyTurn={selectedApprovalForAction.isMyTurn}
        />
      )}
    </div>
  );
};

export default MobileDashboardTab;
